// Common interface for frame metrics
export interface FrameMetrics {
  timestamp: number;
  fps: number;
  deltaTime: number;
}

// Base abstract class for frame collection using Strategy Pattern
abstract class FrameCollector {
  protected rafId: number | null = null;
  protected lastTimestamp: number = 0;

  start(): void {
    this.lastTimestamp = performance.now();
    this.collect();
  }

  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private collect = (): void => {
    const now = performance.now();
    this.onFrame(now);
    this.rafId = requestAnimationFrame(this.collect);
  };

  protected abstract onFrame(timestamp: number): void;
}

// Strategy 1: Aggregate FPS measurement for continuous monitoring
// Updates FPS once per second, returns single aggregated value
export class AggregateCollector extends FrameCollector {
  private frameCount = 0;
  private updateInterval = 1000; // 1 second
  private callback: (fps: number) => void;

  constructor(callback: (fps: number) => void) {
    super();
    this.callback = callback;
  }

  protected onFrame(timestamp: number): void {
    this.frameCount++;
    const elapsed = timestamp - this.lastTimestamp;

    if (elapsed >= this.updateInterval) {
      const fps = Math.round((this.frameCount * 1000) / elapsed);
      this.callback(fps);
      this.frameCount = 0;
      this.lastTimestamp = timestamp;
    }
  }
}

// Strategy 2: Detailed frame-by-frame collection for benchmarking
// Collects all timestamps, allows calculating statistics
export class DetailedCollector extends FrameCollector {
  private timestamps: number[] = [];

  protected onFrame(timestamp: number): void {
    this.timestamps.push(timestamp);
  }

  getFrameMetrics(): FrameMetrics[] {
    const frames: FrameMetrics[] = [];

    for (let i = 1; i < this.timestamps.length; i++) {
      const deltaTime = this.timestamps[i] - this.timestamps[i - 1];
      const fps = deltaTime > 0 ? 1000 / deltaTime : 0;

      frames.push({
        timestamp: this.timestamps[i],
        fps,
        deltaTime
      });
    }

    return frames;
  }

  reset(): void {
    this.timestamps = [];
  }
}
